/**
 * useContextStats — derive the Context line data (plan §B.5).
 *
 * - tokens / % used come ONLY from live UsageEvents: the latest step's
 *   processed tokens (input+output+cache_read+cache_write+reasoning) vs the
 *   model's context_limit from the models.dev catalog.
 * - $ spent = cumulative episode cost: seeded once per episode from
 *   billing.episodes.usage RPC, then raised by live ticks
 *   (deduped by turn/step via the shared client-core accumulator).
 * - Restore rule: after reload the line is typically cost-only; tokens/%
 *   reappear on the first live tick. All-null when no data exists (CON-3).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { UsageEvent, UsageTotals } from "@magnis/client-core";
import { applyUsage, emptyTotals } from "@magnis/client-core";
import type { AppRuntime } from "../../runtime/contracts/runtime";

export interface StatsDeps {
  /** Episode-cumulative cost in micros; null when the episode has none. */
  readonly fetchEpisodeCostMicros: (episodeId: string) => Promise<number | null>;
  /** models.dev context window for a model; null when unknown (CON-3). */
  readonly fetchContextLimit: (
    provider: string,
    model: string,
  ) => Promise<number | null>;
}

interface CatalogModelRow {
  readonly provider_id: string;
  readonly model_id: string;
  readonly contextLimit?: number | null;
}

export function defaultStatsDeps(runtime: AppRuntime): StatsDeps {
  return {
    fetchEpisodeCostMicros: async (episodeId: string): Promise<number | null> => {
      // @tested-by: tst_fe_context_stats_001, tst_fe_context_stats_002
      const body = await runtime.transport.rpc<{
        usage: { cost_micros: number } | null;
      }>("billing.episodes.usage", { episode_id: episodeId });
      return typeof body.usage?.cost_micros === "number"
        ? body.usage.cost_micros
        : null;
    },
    fetchContextLimit: async (
      provider: string,
      model: string,
    ): Promise<number | null> => {
      const result = await runtime.transport.rpc<{
        models: readonly CatalogModelRow[];
      }>("ai_models.catalog_models", { search: model });
      // Exact provider+model match wins; otherwise a model id that is
      // unambiguous across providers is accepted. Ambiguity → unknown (CON-3).
      const byModel = result.models.filter((m) => m.model_id === model);
      const row =
        byModel.find((m) => m.provider_id === provider) ??
        (byModel.length === 1 ? byModel[0] : undefined);
      return typeof row?.contextLimit === "number" ? row.contextLimit : null;
    },
  };
}

export interface ContextStats {
  readonly tokens: number | null;
  readonly percent: number | null;
  readonly costMicros: number | null;
}

function stepTokens(event: UsageEvent): number {
  const c = event.cumulative;
  return c.input + c.output + c.cache_read + c.cache_write + c.reasoning;
}

export function useContextStats(
  runtime: AppRuntime,
  episodeId: string | null,
  enabled: boolean,
  deps: StatsDeps,
): ContextStats {
  const [lastStep, setLastStep] = useState<{
    tokens: number;
    provider: string;
    model: string;
  } | null>(null);
  const [totals, setTotals] = useState<UsageTotals>(emptyTotals);
  const [seededCost, setSeededCost] = useState<number | null>(null);
  const [hasLiveCost, setHasLiveCost] = useState(false);
  const [contextLimit, setContextLimit] = useState<number | null>(null);

  const seenRef = useRef<Set<string>>(new Set());
  const episodeRef = useRef<string | null>(episodeId);
  const limitCacheRef = useRef<Map<string, number | null>>(new Map());
  const [lastEpisodeId, setLastEpisodeId] = useState<string | null>(episodeId);

  // Synchronous reset on episode switch (same pattern as useEpisodeUsage).
  if (lastEpisodeId !== episodeId) {
    setLastEpisodeId(episodeId);
    episodeRef.current = episodeId;
    seenRef.current = new Set();
    setLastStep(null);
    setTotals(emptyTotals());
    setSeededCost(null);
    setHasLiveCost(false);
    setContextLimit(null);
  }

  // Seed $ from the persisted episode aggregate (DEC-9).
  useEffect(() => {
    if (!enabled || !episodeId) return;
    let cancelled = false;
    deps
      .fetchEpisodeCostMicros(episodeId)
      .then((cost): void => {
        if (!cancelled && episodeRef.current === episodeId) setSeededCost(cost);
      })
      .catch((error: unknown) => {
        // Segment stays absent, but the failure is surfaced (NO FALLBACKS:
        // never swallow a real error silently).
        console.warn("episode usage fetch failed:", error);
      });
    return (): void => {
      cancelled = true;
    };
  }, [enabled, episodeId, deps]);

  // Live usage ticks.
  useEffect(() => {
    if (!enabled) return;
    return runtime.agent.chat.onUsage((raw) => {
      const event = raw as UsageEvent;
      if (!episodeRef.current || event.episode_id !== episodeRef.current) return;
      setTotals((prev) => applyUsage(prev, seenRef.current, episodeRef.current, event));
      setLastStep({
        tokens: stepTokens(event),
        provider: event.provider,
        model: event.model,
      });
      if (typeof event.cost_micros === "number") setHasLiveCost(true);
      // Review fix: converge $ after every turn — re-seed from the persisted
      // aggregate and drop live increments, eliminating any seed/live
      // double-count from mid-turn (re)subscription.
      if (event.final && episodeRef.current) {
        const episode = episodeRef.current;
        deps
          .fetchEpisodeCostMicros(episode)
          .then((cost): void => {
            if (episodeRef.current !== episode) return;
            if (cost !== null) {
              setSeededCost(cost);
              setTotals(emptyTotals());
              seenRef.current.clear();
            }
          })
          .catch((error: unknown) => {
            console.warn("episode usage reseed failed:", error);
          });
      }
    });
  }, [enabled, runtime, deps]);

  // Resolve the context window once per model (cached).
  useEffect(() => {
    if (!enabled || !lastStep) return;
    const key = `${lastStep.provider}/${lastStep.model}`;
    const cached = limitCacheRef.current.get(key);
    if (cached !== undefined) {
      setContextLimit(cached);
      return;
    }
    let cancelled = false;
    deps
      .fetchContextLimit(lastStep.provider, lastStep.model)
      .then((limit): void => {
        limitCacheRef.current.set(key, limit);
        if (!cancelled) setContextLimit(limit);
      })
      .catch((error: unknown) => {
        // Unknown limit → % stays absent (CON-3). Not cached: transient
        // failures may recover on the next step.
        console.warn("context limit lookup failed:", error);
      });
    return (): void => {
      cancelled = true;
    };
  }, [enabled, lastStep, deps]);

  return useMemo((): ContextStats => {
    const tokens = lastStep?.tokens ?? null;
    const percent =
      tokens !== null && contextLimit !== null && contextLimit > 0
        ? Math.round((tokens / contextLimit) * 100)
        : null;
    const costKnown = seededCost !== null || hasLiveCost;
    const costMicros = costKnown ? (seededCost ?? 0) + totals.costMicros : null;
    return { tokens, percent, costMicros };
  }, [lastStep, contextLimit, seededCost, hasLiveCost, totals]);
}
